import { FileWarning, Loader2, CheckCircle2, Pencil, XCircle } from "lucide-react";
import { DOC_STATUS, FIELD_STATUS, CONFIDENCE_LABEL, fmtSize, fmtFieldValue } from "./constants";

export function DocumentDetailDialog({
  detail, detailLoading, previewUrl, isConsultant, canWrite,
  acting, editField, onEditFieldChange, onFieldAction, onClose,
}) {
  if (!detail) return null;
  const fields = detail?.fields || [];
  const proposed = fields.filter((f) => ["PROPOSED", "TO_VERIFY", "CONFLICT"].includes(f.review_status));
  const reviewed = fields.filter((f) => ["ACCEPTED", "CORRECTED", "REJECTED"].includes(f.review_status));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" role="dialog" aria-modal="true" data-testid="document-detail-dialog">
      <div className="min-h-full p-4 md:p-8 flex items-start justify-center">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl">
          <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-200">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900 truncate" data-testid="detail-title">
                {detail.document.name}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {fmtSize(detail.document.size)} · caricato il {new Date(detail.document.created_at).toLocaleDateString("it-IT")}
                {detail.document.document_type && <> · tipo: <strong>{detail.document.document_type}</strong></>}
                {isConsultant && detail.document.classification_confidence != null && (
                  <span data-testid="detail-classification-confidence">
                    {" "}· confidenza classificazione: {Math.round(detail.document.classification_confidence * 100)}%
                  </span>
                )}
              </p>
            </div>
            <button type="button" onClick={onClose}
              className="text-slate-400 hover:text-slate-700 text-xl leading-none px-2"
              data-testid="detail-close" aria-label="Chiudi dettaglio">
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Preview (punto 93-95): desktop affiancata, mobile in cima */}
            <div className="p-5 border-b lg:border-b-0 lg:border-r border-slate-200" data-testid="detail-preview">
              {previewUrl ? (
                <iframe src={previewUrl} title="Anteprima documento"
                  className="w-full h-72 lg:h-[32rem] rounded-lg border border-slate-200 bg-slate-50" />
              ) : (
                <div className="w-full h-40 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-sm text-slate-500">
                  {detailLoading ? "Caricamento anteprima…" : "Anteprima non disponibile per questo formato"}
                </div>
              )}
              {(detail.versions || []).length > 0 && (
                <div className="mt-3" data-testid="detail-versions">
                  <p className="text-xs font-semibold text-slate-600 mb-1">Versioni collegate</p>
                  <ul className="text-xs text-slate-500 space-y-0.5">
                    {detail.versions.map((v) => (
                      <li key={v.id}>{v.name} — v{v.version || 1} ({DOC_STATUS[v.document_status]?.label || v.document_status || "caricato"})</li>
                    ))}
                  </ul>
                </div>
              )}
              {(detail.evidence_links || []).length > 0 && (
                <div className="mt-3" data-testid="detail-evidence-links">
                  <p className="text-xs font-semibold text-slate-600 mb-1">Dati collegati a questo documento</p>
                  <ul className="text-xs text-slate-500 space-y-0.5">
                    {detail.evidence_links.map((ev) => (
                      <li key={ev.id}>• {ev.description || ev.name}{ev.page_number ? ` (pag. ${ev.page_number})` : ""}</li>
                    ))}
                  </ul>
                </div>
              )}
              {isConsultant && (detail.conflicts || []).length > 0 && (
                <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3" data-testid="detail-conflicts">
                  <p className="text-xs font-semibold text-red-800 mb-1">Conflitti rilevati</p>
                  {detail.conflicts.map((c) => (
                    <p key={c.id} className="text-xs text-red-700">
                      {c.datapoint_code}: dato approvato {c.approved_value} ≠ documento {c.document_value}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Dati trovati (punto 33: linguaggio semplice) */}
            <div className="p-5" data-testid="detail-fields">
              {detailLoading ? (
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Caricamento…
                </p>
              ) : fields.length === 0 ? (
                <div className="text-center py-8">
                  <FileWarning className="w-7 h-7 text-slate-300 mx-auto mb-2" aria-hidden="true" />
                  <p className="text-sm text-slate-600" data-testid="detail-no-fields">
                    {detail.document.document_status === "PROCESSING"
                      ? "Stiamo analizzando il documento…"
                      : detail.document.document_status === "PROCESSING_ERROR"
                        ? "Non siamo riusciti a leggere questo documento. Puoi inserire i dati manualmente."
                        : "Nessuna informazione estratta da questo documento."}
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm font-semibold text-slate-800 mb-3" data-testid="fields-summary">
                    Abbiamo trovato {fields.length} informazioni.
                    {proposed.length > 0 && <> {proposed.length} {proposed.length === 1 ? "è pronta" : "sono pronte"} per la conferma.</>}
                  </p>
                  <ul className="space-y-3">
                    {fields.map((f) => {
                      const fst = FIELD_STATUS[f.review_status] || FIELD_STATUS.PROPOSED;
                      const pending = ["PROPOSED", "TO_VERIFY", "CONFLICT"].includes(f.review_status);
                      return (
                        <li key={f.id} className="rounded-lg border border-slate-200 p-3" data-testid={`field-row-${f.id}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900">{f.raw_label || f.field_code}</span>
                            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${fst.cls}`} data-testid={`field-status-${f.id}`}>
                              {fst.label}
                            </span>
                            {isConsultant && (
                              <span className="text-xs text-slate-500" data-testid={`field-confidence-${f.id}`}>
                                {CONFIDENCE_LABEL[f.confidence_level] || "—"} ({Math.round((f.confidence || 0) * 100)}%)
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-800 mt-1" data-testid={`field-value-${f.id}`}>
                            <strong>{fmtFieldValue(f)}</strong>
                            {f.raw_value && f.raw_value !== String(f.normalized_value) && (
                              <span className="text-xs text-slate-500"> (nel documento: {f.raw_value})</span>
                            )}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {f.page_number && <span data-testid={`field-page-${f.id}`}>Pagina {f.page_number} · </span>}
                            {f.period_from && f.period_to && <span>Periodo: {f.period_from} – {f.period_to} · </span>}
                          </p>
                          {f.source_text_reference && (
                            <p className="text-xs text-slate-500 italic line-clamp-2 mt-0.5">"{f.source_text_reference}"</p>
                          )}
                          {canWrite && pending && f.proposed_esg_datapoint && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              <button type="button" disabled={acting} onClick={() => onFieldAction(f, "accept")}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-green-700 hover:bg-green-800 disabled:opacity-50 rounded px-2.5 py-1.5 transition-colors"
                                data-testid={`field-accept-${f.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" /> Conferma
                              </button>
                              <button type="button" disabled={acting}
                                onClick={() => onEditFieldChange({ id: f.id, value: f.normalized_value ?? "" })}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-100 rounded px-2.5 py-1.5 transition-colors"
                                data-testid={`field-edit-${f.id}`}>
                                <Pencil className="w-3.5 h-3.5" aria-hidden="true" /> Modifica
                              </button>
                              <button type="button" disabled={acting} onClick={() => onFieldAction(f, "reject")}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:bg-slate-100 rounded px-2.5 py-1.5 transition-colors"
                                data-testid={`field-reject-${f.id}`}>
                                <XCircle className="w-3.5 h-3.5" aria-hidden="true" /> Non è corretto
                              </button>
                            </div>
                          )}
                          {editField?.id === f.id && (
                            <div className="flex items-center gap-2 mt-2" data-testid={`field-edit-form-${f.id}`}>
                              <input type="text" value={editField.value}
                                onChange={(e) => onEditFieldChange({ id: f.id, value: e.target.value })}
                                className="text-sm rounded-md border border-slate-300 px-2 py-1.5 w-40"
                                data-testid={`field-edit-input-${f.id}`} aria-label="Valore corretto" />
                              <button type="button" disabled={acting}
                                onClick={() => onFieldAction(f, "correct", editField.value)}
                                className="text-xs font-semibold text-white bg-green-700 hover:bg-green-800 rounded px-2.5 py-1.5"
                                data-testid={`field-correct-save-${f.id}`}>
                                Salva correzione
                              </button>
                              <button type="button" onClick={() => onEditFieldChange(null)}
                                className="text-xs font-semibold text-slate-500 px-2 py-1.5"
                                data-testid={`field-edit-cancel-${f.id}`}>
                                Annulla
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                  {reviewed.length > 0 && (
                    <p className="text-xs text-slate-500 mt-3">
                      {reviewed.length} {reviewed.length === 1 ? "informazione già revisionata" : "informazioni già revisionate"}.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
